/**
 * GuardianDetailDrawer
 *
 * Side drawer for inspecting a GuardianReunification row. Surfaces the
 * status-workflow buttons (Check In / Verify / Deny) at the bottom and an
 * inline Edit section for verification_method, id_type, is_pre_verified,
 * and authorized_students.
 *
 * ## Compliance gate
 *
 * Restricted-PII tier (guardian contact info). CCPA/CPRA in scope. The
 * backend writes a `guardian_updated` EventAssignmentLog row inside
 * `perform_update` so the privacy audit-log captures the before/after of
 * every edit. The `authorized_students` M2M edit is the sensitive one
 * (adding a student to a guardian's authorized list during an active
 * event) — the drawer surfaces a confirmation prompt before saving that
 * change, citing the audit-log entry that will be written.
 *
 * Status transitions (not_arrived → notified → arrived → checked_in →
 * verified → released) still flow through the dedicated check_in / verify
 * / deny / pre_verify actions because those carry the status broadcasts
 * a raw PATCH would skip.
 *
 * ADR-121 P4-frontend: the typed `NotesPanel` surfaces guardian-subject
 * notes here. `GuardianReunification` has no native note field — the
 * `ReunificationNote.guardian` FK is the wiring; the panel renders
 * whatever the server returns. Restricted
 * notes are absent from the payload when the caller lacks the
 * `visibility_permission` — server is the source of truth, no
 * client-side role check.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Field,
  HStack,
  Input,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  SectionCard,
  SelectInput,
  type StatusType,
} from '@themes/aegix';
import { DrawerComponent } from '../../ui/drawerComponent';
import { DetailRow } from '../DetailRow';
import { toaster } from '../../ui/toaster';
import {
  useCheckInGuardian,
  useVerifyGuardian,
  useDenyGuardian,
  useUpdateEventGuardian,
  useEventStudents,
} from '../../../hooks/queries/useReunification';
import { useFeature } from '../../../hooks/useFeature';
import { FeatureCode } from '../../../types/features';
import {
  ID_TYPE_OPTIONS,
  VERIFICATION_METHOD_OPTIONS,
  type GuardianReunificationWrite,
} from '../../../types/ReunificationTypes';
import { NotesPanel } from '../../reunification/NotesPanel';

interface GuardianRowData {
  id: string;
  name: string;
  status: StatusType;
  status_label: string;
  student_count: number;
  arrived: string;
  verified: string;
  raw_status: string;
  // Editable fields surfaced from the list serializer
  is_pre_verified: boolean;
  raw_verification_method: string;
  raw_id_type: string;
  raw_authorized_students: string[];
  raw_authorized_student_names: string[];
}

interface GuardianDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  guardian: GuardianRowData | undefined;
}

export function GuardianDetailDrawer({
  isOpen,
  onClose,
  eventId,
  guardian,
}: GuardianDetailDrawerProps) {
  const [idType, setIdType] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [denyReason, setDenyReason] = useState('');

  const checkInMutation = useCheckInGuardian(eventId);
  const verifyMutation = useVerifyGuardian(eventId);
  const denyMutation = useDenyGuardian(eventId);
  const updateMutation = useUpdateEventGuardian(eventId);

  // Same backend permission gate as the Add button on the panel.
  // The API enforces `CanOperateGuardianVerification`; this
  // flag just hides Edit when reunification is off site-wide.
  const canManageGuardian = useFeature(FeatureCode.REUNIFICATION);

  // Edit-form local state
  const [editing, setEditing] = useState(false);
  const [editVerificationMethod, setEditVerificationMethod] = useState('');
  const [editIdType, setEditIdType] = useState('');
  const [editIsPreVerified, setEditIsPreVerified] = useState(false);
  const [editAuthorizedStudents, setEditAuthorizedStudents] = useState<
    string[]
  >([]);
  const [showAuthorizedConfirm, setShowAuthorizedConfirm] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');

  // Event students for the authorized-students picker. Scoped to the
  // event so the operator can only pick StudentReunification rows that
  // actually exist on this event.
  const { data: studentsResp } = useEventStudents(eventId, {
    search: studentSearch || undefined,
    page_size: 25,
  });

  const canCheckIn =
    guardian &&
    ['arrived', 'notified', 'en_route', 'pre_verified'].includes(
      guardian.raw_status,
    );
  const canVerify =
    guardian &&
    ['checked_in', 'in_waiting_area', 'matched_to_student'].includes(
      guardian.raw_status,
    );
  const canDeny =
    guardian &&
    !['denied', 'reunified', 'exited', 'released'].includes(
      guardian.raw_status,
    );

  // Reset edit state when the drawer (re)opens on a row, or closes.
  useEffect(() => {
    if (isOpen && guardian) {
      setEditing(false);
      setEditVerificationMethod(guardian.raw_verification_method ?? '');
      setEditIdType(guardian.raw_id_type ?? '');
      setEditIsPreVerified(!!guardian.is_pre_verified);
      setEditAuthorizedStudents(guardian.raw_authorized_students ?? []);
      setShowAuthorizedConfirm(false);
      setStudentSearch('');
    } else if (!isOpen) {
      setEditing(false);
      setShowAuthorizedConfirm(false);
    }
  }, [isOpen, guardian]);

  const initialAuthorizedSet = useMemo(
    () => new Set(guardian?.raw_authorized_students ?? []),
    [guardian],
  );
  const editAuthorizedSet = useMemo(
    () => new Set(editAuthorizedStudents),
    [editAuthorizedStudents],
  );

  const authorizedChanges = useMemo(() => {
    if (!guardian) return { added: [], removed: [] };
    const added = editAuthorizedStudents.filter(
      (id) => !initialAuthorizedSet.has(id),
    );
    const removed = (guardian.raw_authorized_students ?? []).filter(
      (id) => !editAuthorizedSet.has(id),
    );
    return { added, removed };
  }, [guardian, editAuthorizedStudents, initialAuthorizedSet, editAuthorizedSet]);

  const hasChanges = useMemo(() => {
    if (!guardian) return false;
    return (
      editVerificationMethod !== (guardian.raw_verification_method ?? '') ||
      editIdType !== (guardian.raw_id_type ?? '') ||
      editIsPreVerified !== !!guardian.is_pre_verified ||
      authorizedChanges.added.length > 0 ||
      authorizedChanges.removed.length > 0
    );
  }, [
    guardian,
    editVerificationMethod,
    editIdType,
    editIsPreVerified,
    authorizedChanges,
  ]);

  const authorizedListChanged =
    authorizedChanges.added.length > 0 ||
    authorizedChanges.removed.length > 0;

  const handleCheckIn = () => {
    if (!guardian) return;
    checkInMutation.mutate(guardian.id, {
      onSuccess: () => {
        toaster.create({ title: 'Guardian checked in', type: 'success' });
        handleClose();
      },
      onError: (err) => {
        toaster.create({
          title: 'Failed to check in guardian',
          description: err.message,
          type: 'error',
        });
      },
    });
  };

  const handleVerify = () => {
    if (!guardian) return;
    verifyMutation.mutate(
      {
        guardianId: guardian.id,
        data: {
          id_type: idType || undefined,
          id_number: idNumber || undefined,
        },
      },
      {
        onSuccess: () => {
          toaster.create({ title: 'Guardian verified', type: 'success' });
          handleClose();
        },
        onError: (err) => {
          toaster.create({
            title: 'Failed to verify guardian',
            description: err.message,
            type: 'error',
          });
        },
      },
    );
  };

  const handleDeny = () => {
    if (!guardian) return;
    denyMutation.mutate(
      {
        guardianId: guardian.id,
        data: { reason: denyReason || undefined },
      },
      {
        onSuccess: () => {
          toaster.create({ title: 'Guardian denied', type: 'success' });
          handleClose();
        },
        onError: (err) => {
          toaster.create({
            title: 'Failed to deny guardian',
            description: err.message,
            type: 'error',
          });
        },
      },
    );
  };

  const handleEditClick = () => {
    if (!guardian) return;
    setEditVerificationMethod(guardian.raw_verification_method ?? '');
    setEditIdType(guardian.raw_id_type ?? '');
    setEditIsPreVerified(!!guardian.is_pre_verified);
    setEditAuthorizedStudents(guardian.raw_authorized_students ?? []);
    setEditing(true);
  };

  const handleCancelEdit = () => {
    if (guardian) {
      setEditVerificationMethod(guardian.raw_verification_method ?? '');
      setEditIdType(guardian.raw_id_type ?? '');
      setEditIsPreVerified(!!guardian.is_pre_verified);
      setEditAuthorizedStudents(guardian.raw_authorized_students ?? []);
    }
    setEditing(false);
    setShowAuthorizedConfirm(false);
  };

  const submitPatch = () => {
    if (!guardian) return;
    const data: Partial<GuardianReunificationWrite> = {};
    if (editVerificationMethod !== (guardian.raw_verification_method ?? '')) {
      data.verification_method = editVerificationMethod || null;
    }
    if (editIdType !== (guardian.raw_id_type ?? '')) {
      data.id_type = editIdType || null;
    }
    if (editIsPreVerified !== !!guardian.is_pre_verified) {
      data.is_pre_verified = editIsPreVerified;
    }
    if (authorizedListChanged) {
      data.authorized_students = editAuthorizedStudents;
    }
    if (Object.keys(data).length === 0) {
      setEditing(false);
      return;
    }
    updateMutation.mutate(
      { guardianId: guardian.id, data },
      {
        onSuccess: () => {
          toaster.create({ title: 'Guardian updated', type: 'success' });
          setEditing(false);
          setShowAuthorizedConfirm(false);
        },
        onError: (err) => {
          toaster.create({
            title: 'Failed to update guardian',
            description: err.message,
            type: 'error',
          });
          setShowAuthorizedConfirm(false);
        },
      },
    );
  };

  const handleSave = () => {
    // Compliance gate: authorized_students edit is the sensitive
    // operation — show the operator the exact diff + audit-log notice
    // before submitting the PATCH. Other field edits go through without
    // the extra confirm (still audit-logged on the backend).
    if (authorizedListChanged) {
      setShowAuthorizedConfirm(true);
      return;
    }
    submitPatch();
  };

  const handleClose = () => {
    setIdType('');
    setIdNumber('');
    setDenyReason('');
    setEditing(false);
    setShowAuthorizedConfirm(false);
    onClose();
  };

  const toggleStudent = (studentPublicId: string) => {
    setEditAuthorizedStudents((prev) =>
      prev.includes(studentPublicId)
        ? prev.filter((id) => id !== studentPublicId)
        : [...prev, studentPublicId],
    );
  };

  // Resolve student names from the loaded event-students list for the
  // diff preview. Falls back to the public_id when the row isn't on the
  // current page of results.
  const resolveStudentName = (studentPublicId: string): string => {
    const row = (studentsResp?.results ?? []).find(
      (s) => s.public_id === studentPublicId,
    );
    if (!row) return studentPublicId;
    return row.student_name || studentPublicId;
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={handleClose}
      title="Guardian Detail"
      size="md"
      closeOnOutside={false}
      closeOnEsc={false}
      footer={
        <HStack gap={3} width="100%">
          {canCheckIn && (
            <ButtonComponent
              visual="primary"
              onClick={handleCheckIn}
              disabled={checkInMutation.isPending}
              data-e2e="check-in-button">
              {checkInMutation.isPending ? 'Checking In...' : 'Check In'}
            </ButtonComponent>
          )}
          {canVerify && (
            <ButtonComponent
              visual="primary"
              onClick={handleVerify}
              disabled={verifyMutation.isPending}
              data-e2e="verify-button">
              {verifyMutation.isPending ? 'Verifying...' : 'Verify'}
            </ButtonComponent>
          )}
          {canDeny && (
            <ButtonComponent
              visual="alert"
              onClick={handleDeny}
              disabled={denyMutation.isPending}
              data-e2e="deny-button">
              {denyMutation.isPending ? 'Denying...' : 'Deny'}
            </ButtonComponent>
          )}
          <ButtonComponent visual="secondary" onClick={handleClose} data-e2e="close-button">
            Close
          </ButtonComponent>
        </HStack>
      }
    >
      {guardian ? (
        <Flex direction="column" gap={4}>
          <SectionCard title="Guardian Details">
            <VStack align="stretch" gap={0}>
              <DetailRow label="Name" value={guardian.name} dataE2e="guardian-detail-name" />
              <DetailRow label="Status" value={guardian.status_label} dataE2e="guardian-detail-status" />
              <DetailRow
                label="Pre-Verified"
                value={guardian.is_pre_verified ? 'Yes' : 'No'}
                dataE2e="guardian-detail-pre-verified"
              />
              <DetailRow
                label="Verification Method"
                value={
                  VERIFICATION_METHOD_OPTIONS.find(
                    (o) => o.value === guardian.raw_verification_method,
                  )?.label ||
                  guardian.raw_verification_method ||
                  '---'
                }
                dataE2e="guardian-detail-verification-method"
              />
              <DetailRow
                label="ID Type"
                value={
                  ID_TYPE_OPTIONS.find(
                    (o) => o.value === guardian.raw_id_type,
                  )?.label ||
                  guardian.raw_id_type ||
                  '---'
                }
                dataE2e="guardian-detail-id-type"
              />
              <DetailRow
                label="Authorized Students"
                value={
                  guardian.raw_authorized_student_names &&
                  guardian.raw_authorized_student_names.length > 0
                    ? guardian.raw_authorized_student_names.join(', ')
                    : String(guardian.student_count)
                }
                dataE2e="guardian-detail-authorized-students"
              />
              <DetailRow label="Arrived" value={guardian.arrived} dataE2e="guardian-detail-arrived" />
              <DetailRow label="Verified" value={guardian.verified} dataE2e="guardian-detail-verified" />
            </VStack>
          </SectionCard>

          {/* ADR-121 P4-frontend: typed/tagged notes via NotesPanel.
              GuardianReunification has no native note field — the
              ReunificationNote.guardian FK is the wiring. Server is the
              source of truth for visibility — restricted notes simply don't
              appear in the response when the caller lacks the
              permission. */}
          <NotesPanel
            eventId={eventId}
            subject={{ guardian: guardian.id }}
            e2ePrefix="guardian-notes-panel"
          />

          {canManageGuardian && !editing && (
            <HStack justify="flex-end" gap={2}>
              <ButtonComponent
                visual="secondary"
                size="sm"
                onClick={handleEditClick}
                data-e2e="guardian-edit-button"
              >
                Edit details
              </ButtonComponent>
            </HStack>
          )}

          {canManageGuardian && editing && (
            <SectionCard title="Edit details">
              <VStack align="stretch" gap={4}>
                <SelectInput
                  label="Verification Method"
                  name="guardian_edit_verification_method"
                  value={editVerificationMethod}
                  onChange={(val: string) => setEditVerificationMethod(val)}
                  option={VERIFICATION_METHOD_OPTIONS}
                  placeholder="Select verification method"
                  e2eTitle="guardian-edit-verification-method"
                />

                <SelectInput
                  label="ID Type"
                  name="guardian_edit_id_type"
                  value={editIdType}
                  onChange={(val: string) => setEditIdType(val)}
                  option={ID_TYPE_OPTIONS}
                  placeholder="Select ID type"
                  e2eTitle="guardian-edit-id-type"
                />

                <Field.Root>
                  <HStack justify="space-between" align="center" width="100%">
                    <Field.Label fontSize="sm" color="text.primary">
                      Pre-verified
                    </Field.Label>
                    <Switch.Root
                      checked={editIsPreVerified}
                      onCheckedChange={(details) =>
                        setEditIsPreVerified(details.checked)
                      }
                      disabled={updateMutation.isPending}
                    >
                      <Switch.HiddenInput
                        data-e2e="guardian-edit-pre-verified"
                      />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                    </Switch.Root>
                  </HStack>
                  <Field.HelperText fontSize="xs" color="text.secondary">
                    Status (notified / arrived / checked-in / verified)
                    still changes via the workflow buttons — those carry
                    the AAS-712 broadcasts.
                  </Field.HelperText>
                </Field.Root>

                <Box>
                  <Field.Label fontSize="sm" color="text.primary" mb={1}>
                    Authorized Students
                  </Field.Label>
                  <Field.HelperText fontSize="xs" color="text.secondary" mb={2}>
                    Sensitive: changes here are audit-logged. Confirmation
                    is required before saving (CCPA/CPRA).
                  </Field.HelperText>
                  <Input
                    size="sm"
                    placeholder="Search students by name…"
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    data-e2e="guardian-edit-authorized-students-search"
                    mb={2}
                  />
                  <Box
                    borderWidth="1px"
                    borderColor="border.subtle"
                    borderRadius="md"
                    maxHeight="180px"
                    overflowY="auto"
                    data-e2e="guardian-edit-authorized-students-list"
                  >
                    {(studentsResp?.results ?? []).length === 0 ? (
                      <Box p={3}>
                        <Text fontSize="xs" color="text.tertiary">
                          {studentSearch
                            ? 'No matching students on this event.'
                            : 'No students on this event.'}
                        </Text>
                      </Box>
                    ) : (
                      <VStack align="stretch" gap={0}>
                        {(studentsResp?.results ?? []).map((s) => {
                          const isChecked = editAuthorizedStudents.includes(
                            s.public_id,
                          );
                          return (
                            <HStack
                              key={s.public_id}
                              px={3}
                              py={2}
                              cursor="pointer"
                              bg={isChecked ? 'bg.brandMuted' : undefined}
                              _hover={{
                                bg: isChecked ? 'bg.brandMuted' : 'bg.muted',
                              }}
                              onClick={() => toggleStudent(s.public_id)}
                              data-e2e={`guardian-edit-authorized-student-row-${s.public_id}`}
                              data-checked={isChecked ? 'true' : undefined}
                            >
                              <Text fontSize="sm" color="text.primary" flex={1}>
                                {s.student_name}
                              </Text>
                              <Text fontSize="xs" color="text.secondary">
                                {isChecked ? '✓ Authorized' : 'Tap to add'}
                              </Text>
                            </HStack>
                          );
                        })}
                      </VStack>
                    )}
                  </Box>
                </Box>

                <HStack justify="flex-end" gap={2}>
                  <ButtonComponent
                    visual="secondary"
                    size="sm"
                    onClick={handleCancelEdit}
                    disabled={updateMutation.isPending}
                    data-e2e="guardian-edit-cancel"
                  >
                    Cancel
                  </ButtonComponent>
                  <ButtonComponent
                    visual="primary"
                    size="sm"
                    onClick={handleSave}
                    disabled={updateMutation.isPending || !hasChanges}
                    data-e2e="guardian-edit-save"
                  >
                    {updateMutation.isPending ? 'Saving…' : 'Save'}
                  </ButtonComponent>
                </HStack>
              </VStack>
            </SectionCard>
          )}

          {showAuthorizedConfirm && (
            <SectionCard title="Confirm sensitive change (CCPA/CPRA)">
              <VStack align="stretch" gap={3}>
                <Text fontSize="sm" color="text.primary">
                  You are about to change which students this guardian is
                  authorized to pick up. This change will be written to the
                  privacy audit log with your user account and the exact
                  diff below.
                </Text>
                {authorizedChanges.added.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight={600} color="text.success" mb={1}>
                      Adding ({authorizedChanges.added.length}):
                    </Text>
                    <VStack align="stretch" gap={1}>
                      {authorizedChanges.added.map((id) => (
                        <Text
                          key={id}
                          fontSize="xs"
                          color="text.secondary"
                          data-e2e={`guardian-edit-confirm-added-${id}`}
                        >
                          + {resolveStudentName(id)}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                )}
                {authorizedChanges.removed.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight={600} color="text.danger" mb={1}>
                      Removing ({authorizedChanges.removed.length}):
                    </Text>
                    <VStack align="stretch" gap={1}>
                      {authorizedChanges.removed.map((id) => (
                        <Text
                          key={id}
                          fontSize="xs"
                          color="text.secondary"
                          data-e2e={`guardian-edit-confirm-removed-${id}`}
                        >
                          − {resolveStudentName(id)}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                )}
                <HStack justify="flex-end" gap={2}>
                  <ButtonComponent
                    visual="secondary"
                    size="sm"
                    onClick={() => setShowAuthorizedConfirm(false)}
                    disabled={updateMutation.isPending}
                    data-e2e="guardian-edit-confirm-cancel"
                  >
                    Back
                  </ButtonComponent>
                  <ButtonComponent
                    visual="primary"
                    size="sm"
                    onClick={submitPatch}
                    disabled={updateMutation.isPending}
                    data-e2e="guardian-edit-confirm-save"
                  >
                    {updateMutation.isPending
                      ? 'Saving…'
                      : 'Confirm & save'}
                  </ButtonComponent>
                </HStack>
              </VStack>
            </SectionCard>
          )}

          {canVerify && !editing && (
            <SectionCard title="Verification Info">
              <VStack align="stretch" gap={3}>
                <Field.Root gap={0}>
                  <Field.Label fontSize="sm" color="text.secondary" mb={1}>
                    ID Type
                  </Field.Label>
                  <Input
                    size="sm"
                    placeholder="e.g. Driver's License"
                    value={idType}
                    onChange={(e) => setIdType(e.target.value)}
                    data-e2e="guardian-detail-id-type-input"
                  />
                </Field.Root>
                <Field.Root gap={0}>
                  <Field.Label fontSize="sm" color="text.secondary" mb={1}>
                    ID Number
                  </Field.Label>
                  <Input
                    size="sm"
                    placeholder="e.g. DL12345678"
                    value={idNumber}
                    onChange={(e) => setIdNumber(e.target.value)}
                    data-e2e="guardian-detail-id-number"
                  />
                </Field.Root>
              </VStack>
            </SectionCard>
          )}

          {canDeny && !editing && (
            <SectionCard title="Deny Reason">
              <Input
                size="sm"
                aria-label="Deny reason"
                placeholder="Reason for denial (optional)"
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                data-e2e="guardian-detail-deny-reason"
              />
            </SectionCard>
          )}
        </Flex>
      ) : null}
    </DrawerComponent>
  );
}
